import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CustomAxios from '../utility/customAxios';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_PATHS } from '../routes/Routes';
import PasswordValidator from '../components/PasswordValidator';
import { EMAIL_REGEX } from '../utility/inputUtils';
import { externalFormStyling } from '../styles/sxStyling';

function InvalidInviteDialog({ open }: { open: boolean }) {
  const navigate = useNavigate();

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Sign Up Error</DialogTitle>
      <DialogContent>
        <Typography sx={{ mx: 2 }}>The invitation is invalid. It may have expired or the invite link may be incorrect.</Typography>
        <Typography sx={{ mx: 2, mt: 2 }}>Please check the link you used to get here or contact your administrator to get a new one.</Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', mt: 2 }}>
        <Button variant="contained" color="primary" onClick={() => navigate(ROUTE_PATHS.LOGIN)}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type SignUpFormState = {
  email: string;
  password: string;
  verifyPassword: string;
};

type ErrorState = {
  email: boolean;
  password: boolean;
  token: boolean;
};

export default function SignUp() {
  const [formData, setFormData] = useState<SignUpFormState>({
    email: '',
    password: '',
    verifyPassword: '',
  });
  const [errors, setErrors] = useState<ErrorState>({
    email: false,
    password: true,
    token: false,
  });
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);

  const { uuid } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const { email } = formData;
    setErrors({ ...errors, email: !(_.isEmpty(email) || EMAIL_REGEX.test(email)) });
  }, [formData.email]);

  useEffect(() => {
    CustomAxios.get(`sign-up/${uuid}`).catch(() => setErrors({ ...errors, token: true }));
  }, []);

  const onSend = async () => {
    try {
      CustomAxios.post('sign-up', { uuid, email: formData.email, password: formData.password });
      setShowSnackbar(true);
      setTimeout(() => navigate(ROUTE_PATHS.LOGIN), 3 * 1000);
    } catch (e) {
      alert('There was an error creating your account. Please try again or contact support.');
    }
  };

  const snackbarMessage = (
    <Typography>
      Your your credentials have been set up!
      <br />
      You will be redirected to the login page.
    </Typography>
  );

  return (
    <Container component="main" maxWidth="xs">
      <title>ScheduLink | Studio Management Software for Volume Photo Studios</title>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '70vh',
        }}
      >
        <Typography component="h1" variant="h5">
          Sign Up
        </Typography>
        <Box sx={externalFormStyling}>
          <Typography>Please enter your login details</Typography>
          <TextField
            autoFocus
            required
            label="Email Address"
            value={formData.email}
            onChange={(event) => setFormData({ ...formData, email: event.target.value })}
            error={errors.email}
            helperText={errors.email && 'Please enter a valid email'}
          />
          <TextField
            autoFocus
            required
            label="Password"
            type="password"
            value={formData.password}
            onChange={(event) => setFormData({ ...formData, password: event.target.value })}
          />
          <TextField
            required
            label="Verify Password"
            type="password"
            value={formData.verifyPassword}
            onChange={(event) => setFormData({ ...formData, verifyPassword: event.target.value })}
          />
          <PasswordValidator
            password={formData.password}
            validatePassword={formData.verifyPassword}
            validCallback={(passwordValid) => setErrors({ ...errors, password: !passwordValid })}
          />
          <Button fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} onClick={onSend} disabled={_.some(_.values(errors))}>
            Sign Up
          </Button>
        </Box>
      </Box>
      <Snackbar open={showSnackbar} onClose={() => setShowSnackbar(false)} message={snackbarMessage} />
      <InvalidInviteDialog open={errors.token} />
    </Container>
  );
}
